<template>
    <div class="product-class-peers">
        <AonCard class="h-100">
            <div class="card-content h-100">
                <AonCoverLoading
                    :loading="loadingPeers"
                    :title="`Loading Top Innovators for ${entityStore.entity.name}'s Products`"
                    message="This might take a few minutes if a company hasn't been analyzed before."
                ></AonCoverLoading>
                <div v-if="!loadingPeers" class="display-flex h-100 flex-wrap">
                    <div
                        class="nav-holder mt-md display-flex align-items-start w-100 justify-between"
                    >
                        <div class="drop-holder display-flex align-items-center mt-n4">
                            <h6 class="mt-n4 font-bold" style="min-width: 180px">
                                Top Innovators for:
                            </h6>
                            <AonDropdown
                                :key="chartUpdater"
                                style="min-width: 250px"
                                class="option-dropdown mb-md"
                                :class="{ disabled: loadingProduct }"
                                :items="productListDropdown"
                                label=""
                                :init-selection="
                                    productListDropdown &&
                                    productListDropdown[currentProductIdIndex]
                                        ? productListDropdown[currentProductIdIndex]
                                        : null
                                "
                                @select-option="onSelectOption"
                            />
                        </div>
                        <div
                            class="mt-n3 display-flex align-items-center justify-end"
                            :class="{ disabled: loadingProduct }"
                        >
                            <div class="nav-dots display-flex align-items-center">
                                <font-awesome-icon
                                    icon="fas fa-circle-arrow-left"
                                    class="carousel-icon mr-xs text-moat-600 align-self-center cursor-pointer feedback-icon"
                                    size="2xl"
                                    @click="decrementIndex(true)"
                                />
                                <div
                                    v-for="(product, index) in productList"
                                    class="dot mx-xxs"
                                    :class="{ current: currentProductIdIndex === index }"
                                ></div>
                                <font-awesome-icon
                                    icon="fas fa-circle-arrow-right"
                                    class="carousel-icon ml-xs text-moat-600 align-self-center cursor-pointer feedback-icon"
                                    size="2xl"
                                    @click="incrementIndex(true)"
                                />
                            </div>
                        </div>
                    </div>
                    <div
                        class="details-holder display-flex w-100 flex-grow-1"
                        style="position: relative"
                    >
                        <AonCoverLoading
                            :loading="loadingProduct"
                            :title="`Fetching Top Innovators for ${productList[currentProductIdIndex].product}`"
                        ></AonCoverLoading>
                        <div class="chart-holder">
                            <ProductClassPeerChart
                                :key="chartUpdater"
                                :peer-data="peerData[currentProductIdIndex]"
                                @chart-clicked="onUserEvent"
                            />
                        </div>
                        <div class="table-holder w-100 display-flex flex-column">
                            <ProductClassPeerTable
                                :peer-data="peerData[currentProductIdIndex]"
                                @table-event="onUserEvent"
                            />
                        </div>
                    </div>
                </div>
            </div>
        </AonCard>
    </div>
</template>
<script setup>
import { ref, inject, onMounted, watch, computed, onUnmounted } from 'vue'
import { useEntityStore, useMoat2ProductStore } from '@/stores'
import { useRoute } from 'vue-router'

import { getTopProductsByInnovation } from '@/api/companyDashboard.js'
import { getCompetitorsByProduct } from '@/api/productAlignment.js'
import ProductClassPeerTable from './ProductClassPeerTable.vue'
import ProductClassPeerChart from './ProductClassPeerChart.vue'

import {
    dataTeal,
    dataAqua,
    dataCobalt,
    dataMarine,
    dataMagenta,
    dataRaspberry,
    dataOrange,
    dataYellow,
    dataLime,
    dataGreen,
} from '@/styles/js_variables.module.scss'

const entityStore = useEntityStore()
const moat2ProductStore = useMoat2ProductStore()
const logger = inject('logger')
const eventBus = inject('eventBus')
const route = useRoute()

const peerData = ref([[], [], [], [], []])
const loadingPeers = ref(true)
const loadingProduct = ref(false)
const productList = ref([])
const currentProductIdIndex = ref(0)
const chartUpdater = ref(0)
const autoTimer = ref(null)
const userStoppedAutoTimer = ref(false)
const productListDropdown = ref(null)

onMounted(() => {
    loadingPeers.value = true
})

onUnmounted(() => {
    if (autoTimer.value) {
        clearInterval(autoTimer.value)
    }
    eventBus.off('set-arena-space-loading')
})

const setColors = computed(() => {
    let baseColors = [
        dataTeal,
        dataRaspberry,
        dataOrange,
        dataYellow,
        dataLime,
        dataGreen,
        dataMagenta,
        dataMarine,
        dataCobalt,
        dataAqua,
    ]
    return baseColors
})

const getTopProducts = async () => {
    loadingPeers.value = true
    try {
        const { data } = await getTopProductsByInnovation(entityStore.entity.aon_entity_pk)
        productList.value = data
        productListDropdown.value = data.map((item, index) => ({
            itemText: item.product,
            value: item.product,
            index: index,
        }))
        getCompetitorData(data)
    } catch (error) {
        logger.error(error)
    }
}

const getCompetitorData = async (products = null) => {
    if (
        peerData.value[currentProductIdIndex.value] === undefined ||
        peerData.value[currentProductIdIndex.value].length === 0
    ) {
        if (products !== null) {
            loadingPeers.value = true
        } else {
            loadingProduct.value = true
        }

        try {
            await getCompetitorsByProduct(
                route.params.jobPk ? route.params.jobPk : moat2ProductStore.alignmentPk,
                [productList.value[currentProductIdIndex.value].nodeId],
                null,
                true,
                6
            )
                .then((response) => {
                    if (!entityStore.entity) {
                        return
                    }

                    const currentEntityName = entityStore.entity.name
                    peerData.value[currentProductIdIndex.value] = response.data
                        .sort((a, b) => {
                            if (a.aonEntityName === currentEntityName) return -1
                            if (b.aonEntityName === currentEntityName) return 1
                            return b.powerScore - a.powerScore
                        })
                        .map((item, index) => ({
                            ...item,
                            color: setColors.value[index],
                        }))
                })
                .catch((error) => {
                    logger.error(error)
                })
        } catch (err) {
            logger.error(err)
        } finally {
            loadingPeers.value = false
            loadingProduct.value = false
            chartUpdater.value++

            if (!autoTimer.value) {
                if (!userStoppedAutoTimer.value) {
                    startAutoTimer()
                }
            }
        }
    } else {
        chartUpdater.value++

        if (!autoTimer.value) {
            if (!userStoppedAutoTimer.value) {
                startAutoTimer()
            }
        }
    }
}

const onSelectOption = (option) => {
    userStoppedAutoTimer.value = true

    if (userStoppedAutoTimer.value) {
        clearInterval(autoTimer.value)
        autoTimer.value = null
    }

    currentProductIdIndex.value = option.index
}

const incrementIndex = (userClicked = false) => {
    if (userClicked) {
        clearInterval(autoTimer.value)
        autoTimer.value = null
        userStoppedAutoTimer.value = true
    }
    if (autoTimer.value) {
        clearInterval(autoTimer.value)
        autoTimer.value = null
    }
    if (currentProductIdIndex.value < productList.value.length - 1) {
        currentProductIdIndex.value++
    } else {
        currentProductIdIndex.value = 0
    }
}

const decrementIndex = (userClicked = false) => {
    if (userClicked) {
        clearInterval(autoTimer.value)
        autoTimer.value = null
        userStoppedAutoTimer.value = true
    }
    if (autoTimer.value) {
        clearInterval(autoTimer.value)
        autoTimer.value = null
    }

    if (currentProductIdIndex.value > 0) {
        currentProductIdIndex.value--
    } else {
        currentProductIdIndex.value = productList.value.length - 1
    }
}

const startAutoTimer = () => {
    autoTimer.value = setInterval(() => {
        incrementIndex()
    }, 10000)
}

const onUserEvent = (event) => {
    clearInterval(autoTimer.value)
    autoTimer.value = null
    userStoppedAutoTimer.value = true
}

watch(
    () => currentProductIdIndex.value,
    (newVal, oldVal) => {
        getCompetitorData()
    }
)

watch(
    [() => moat2ProductStore.alignmentPk, () => moat2ProductStore.productAlignmentLoading],
    ([newAlignmentPk, newLoading], [oldAlignmentPk, oldLoading]) => {
        if (newAlignmentPk && !newLoading) {
            getTopProducts()
        }
    },
    { immediate: true }
)
</script>

<style lang="scss" scoped>
.product-class-peers {
    width: 100%;
    height: 420px;
    position: relative;

    :deep(.aon-dropdown) {
        .trigger {
            width: calc(100% + 15px);
        }
    }

    .dot {
        width: 16px;
        height: 16px;
        border-radius: 50%;
        background: var(--color-stone-200);

        &.current {
            background: var(--color-moat-900);
        }
    }

    .chart-holder {
        width: 20%;
    }

    .table-holder {
        width: 80%;
    }
}
</style>
